/* eslint-disable no-magic-numbers */
import { ScreenThumbnails } from "../api/model/snapshots/Snapshot";
import Color from "../model/color";
import { ASSET_URL_BASE } from "../constants";
import { Theme } from "../theme";

import BasicRecord from "./BasicRecord";
import { uppercaseFirst } from "./string";

interface SnapshotThumbnailSourceData {
    status: "ready" | "processing" | "error";
    src?: string;
    srcset?: string;
    screenId?: string;
}

function imageUrl(imgPath: string): string {
    if (ASSET_URL_BASE as string === "/") {
        return new URL(imgPath, location.origin).href;
    }

    let normalizedImgPath = imgPath;
    if (normalizedImgPath.startsWith("/")) {
        normalizedImgPath = imgPath.substring(1);
    }

    return new URL(`${ASSET_URL_BASE}${normalizedImgPath}`).href;
}

function srcset(imageName: string, descriptors = ["2x", "3x"]): string | undefined {
    const parts = imageName.match(/(.*)\.([^.]+)/);

    if (parts?.length !== 3) {
        return;
    }

    return descriptors.map(d => `${parts[1]}@${d}.${parts[2]} ${d}`).join(", ");
}

function emotarUrl(emotar: string, activeTheme: Theme): string {
    if (emotar === "random") {
        return imageUrl(`/img/emotars/${activeTheme}/icRandom.svg`);
    }

    const name = uppercaseFirst(emotar.replace(/_\w/g, m => m.charAt(1).toUpperCase()));

    return imageUrl(`/img/emotars/${activeTheme}/emotar${name}.png`);
}

function cropImage(
    originalSrc: string,
    options: {
        width: number;
        height: number;
    }
): Promise<string> {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        canvas.width = options.width;
        canvas.height = options.height;

        const ctx = canvas.getContext("2d")!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "medium";

        const sourceImg = document.createElement("img");
        sourceImg.crossOrigin = "anonymous";

        let destWidth;
        let sourceHeight;
        let destHeight;
        let scaleFactor = 1;
        let destX = 0;
        let destY = 0;

        sourceImg.addEventListener("load", () => {
            if (sourceImg.width <= canvas.width) {
                destWidth = sourceImg.width;
                destX = (canvas.width - sourceImg.width) / 2;
            } else {
                scaleFactor = canvas.width / sourceImg.width;
                destWidth = canvas.width;
            }

            if (!options.height) {
                // No specified height means just scale
                canvas.height = canvas.width * sourceImg.height / sourceImg.width;
            }

            destHeight = scaleFactor * sourceImg.height;

            if (destHeight <= canvas.height) {
                sourceHeight = sourceImg.height;
                destY = (canvas.height - destHeight) / 2;
            } else {
                sourceHeight = canvas.height / scaleFactor;
                destHeight = canvas.height;
            }

            try {
                ctx.drawImage(sourceImg, 0, 0, sourceImg.width, sourceHeight, destX, destY, destWidth, destHeight);
                canvas.toBlob(blob => {
                    resolve(URL.createObjectURL(blob!));
                });
            } catch (err) {
                reject(err);
            }
        });

        sourceImg.addEventListener("error", () => {
            resolve(originalSrc);
        });

        sourceImg.src = originalSrc;
    });
}

/**
 * Builds the src and srcset for an image URL to be resized
 * according to options
 */
function getResizedImageSources(url: string, { width, height }: {
    width: number;
    height?: number;
}): {
    src: string;
    srcset: string;
} {
    const resizeURL = `${Zeplin.urls.imgURL}/${encodeURIComponent(url)}`;
    let query = `?w=${width}`;
    let query2x = `?w=${width * 2}`;

    if (height) {
        query += `&cropTop=0&cropLeft=0&cropWidth=${width}&cropHeight=${height}`;
        query2x += `&cropTop=0&cropLeft=0&cropWidth=${width * 2}&cropHeight=${height * 2}`;
    }

    return {
        src: `${resizeURL}${query}`,
        srcset: `${resizeURL}${query2x} 2x`
    };
}

function getSnapshotThumbnailSourceData(
    thumbnailData?: ScreenThumbnails
): SnapshotThumbnailSourceData | undefined {
    if (!thumbnailData) {
        return undefined;
    }

    const targetResource = window.devicePixelRatio <= 1
        ? thumbnailData.small
        : thumbnailData.medium;

    if (!targetResource?.url) {
        return {
            status: targetResource?.status ?? "error"
        };
    }

    // extracting screen url id
    const screenId = targetResource.url.split("cache/")[1].slice(0, -4);

    return {
        status: targetResource.status,
        src: thumbnailData.small?.url,
        srcset: thumbnailData.medium?.url ? `${thumbnailData.medium?.url} 2x` : undefined,
        screenId
    };
}

const LIGHTNESS_THRESHOLD = 200;
const NEXT_PIXEL_OFFSET = 4;
const FULL_SATURATION = 255;
const R_OFFSET = 0;
const G_OFFSET = 1;
const B_OFFSET = 2;
const A_OFFSET = 3;

function isLight(imageData: Uint8ClampedArray): boolean {
    for (let i = 0; i < imageData.length; i += NEXT_PIXEL_OFFSET) {
        const red = imageData[i + R_OFFSET];
        const green = imageData[i + G_OFFSET];
        const blue = imageData[i + B_OFFSET];
        const alpha = imageData[i + A_OFFSET];

        if (
            alpha >= FULL_SATURATION - LIGHTNESS_THRESHOLD &&
            (
                red < LIGHTNESS_THRESHOLD ||
                green < LIGHTNESS_THRESHOLD ||
                blue < LIGHTNESS_THRESHOLD
            )
        ) {
            return false;
        }
    }

    return true;
}

enum ImageLightness {
    Light = "light",
    Dark = "dark"
}

type ImageProperties = {
    size: {
        width: number;
        height: number;
    };
    lightness: ImageLightness;
};

const imagePropertyCache: BasicRecord<ImageProperties> = {};

function getImageProperties(imgOrURL: string | HTMLImageElement): Promise<ImageProperties> {
    return new Promise((resolve, reject) => {
        const cacheKey = typeof imgOrURL === "string" ? imgOrURL : imgOrURL.src;
        if (cacheKey in imagePropertyCache) {
            resolve(imagePropertyCache[cacheKey]);
        } else {
            const img = typeof imgOrURL === "string"
                ? document.createElement("img")
                : imgOrURL;

            img.crossOrigin = "anonymous";

            img.addEventListener("load", function readImageData() {
                img.removeEventListener("load", readImageData);

                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d")!;
                canvas.width = img.width;
                canvas.height = img.height;
                try {
                    ctx.drawImage(img, 0, 0);
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                    const lightness: ImageLightness = isLight(imageData)
                        ? ImageLightness.Light
                        : ImageLightness.Dark;

                    const properties = {
                        size: {
                            width: img.width,
                            height: img.height
                        },
                        lightness
                    };

                    imagePropertyCache[cacheKey] = properties;
                    resolve(properties);
                } catch (err) {
                    reject(err);
                }
            });

            img.addEventListener("error", function handleError(ev) {
                img.removeEventListener("error", handleError);
                reject(ev);
            });

            if (typeof imgOrURL === "string") {
                img.src = imgOrURL;
            }
        }
    });
}

/**
 * Returns the dominant color of an image in hex format
 *
 * @param image element
 * @returns color in hex format
 */
function getDominantColorOfImage(imgEl: HTMLImageElement): string | null {
    if (!imgEl) {
        return null;
    }

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d")!;

    canvas.height = imgEl.naturalHeight || imgEl.offsetHeight || imgEl.height;
    canvas.width = imgEl.naturalWidth || imgEl.offsetWidth || imgEl.width;
    try {
        context.drawImage(imgEl, 0, 0);
    } catch (e) {
        return null;
    }

    let data: Uint8ClampedArray | undefined;
    try {
        ({ data } = context.getImageData(0, 0, canvas.width, canvas.height));
    } catch (e) {
        return null;
    }

    const { length } = data;
    const RGBA_OFFSET = 4;
    const colorDict: BasicRecord<number> = {};

    for (let i = 0; i < length; i += RGBA_OFFSET) {
        const red = data[i];
        const green = data[i + 1];
        const blue = data[i + 2];
        const alpha = data[i + 3];

        // Pass if the color is transparent
        if (alpha === 0) {
            continue;
        }

        // Pass grayscale colors
        if (red === green && green === blue) {
            continue;
        }

        const key = `#${[red, green, blue].map(Color.componentToHex).join("")}`;
        if (colorDict[key]) {
            colorDict[key]++;
        } else {
            colorDict[key] = 1;
        }
    }

    const colorKeys = Object.keys(colorDict);
    if (!colorKeys.length) {
        return null;
    }

    return colorKeys.reduce((max, next) =>
        (colorDict[max] > colorDict[next] ? max : next)
    );
}

const FIXED_CROP_HEIGHT = 200;
const CROP_WIDTH = 256;
const CROP_MAX_HEIGHT = 520;

function calculateCropHeight({
    width, height, fixedSize = false, cropRatio = 1, cropHeight = FIXED_CROP_HEIGHT
}: {
    width: number; height: number; fixedSize?: boolean; cropRatio?: number; cropHeight?: number;
}): number {
    return fixedSize
        ? cropHeight
        : Math.min(Math.round((CROP_WIDTH / width) * height), CROP_MAX_HEIGHT) * cropRatio;
}

export {
    imageUrl,
    emotarUrl,
    srcset,
    cropImage,
    getResizedImageSources,
    getSnapshotThumbnailSourceData,
    isLight,
    getImageProperties,
    getDominantColorOfImage,
    calculateCropHeight
};

export {
    ImageLightness,
    SnapshotThumbnailSourceData
};
